import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

function display(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

export default function RecordHistoryDialog({ target, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) {
      setItems([]);
      return;
    }
    let mounted = true;
    setLoading(true);
    api
      .get(`/hrms/history/${target.kind}/${target.id}`)
      .then(({ data }) => {
        if (mounted) {
          setItems(data.items || []);
        }
      })
      .catch((error) => toast.error(apiErr(error)))
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [target]);

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle>Record history</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[58vh] pr-3" data-testid="hrms-record-history-dialog">
          <div className="space-y-3">
            {loading && (
              <div className="text-sm text-muted-foreground" data-testid="hrms-record-history-loading">
                Loading record history…
              </div>
            )}
            {!loading && !items.length && (
              <div className="text-sm text-muted-foreground" data-testid="hrms-record-history-empty">
                No recorded changes yet.
              </div>
            )}
            {items.map((item) => (
              <div
                key={item.id}
                className="border-l-2 border-[#255058] py-2 pl-3"
                data-testid={`hrms-history-entry-${item.id}`}
              >
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-medium">{item.user_name || "System"}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {item.field || item.action.replace(/_/g, " ")}
                </div>
                <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Old: </span>
                    <span className="break-words">{display(item.previous_value)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">New: </span>
                    <span className="break-words">{display(item.new_value)}</span>
                  </div>
                </div>
                {item.reason && (
                  <div className="mt-2 text-xs">
                    <span className="font-medium">Reason: </span>
                    {item.reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hrms-history-close-button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}